/**
 * CertStatusPanel.jsx
 * 
 * Shows certificate monitoring status for devices with cert monitoring enabled.
 * Displayed on the Health page.
 * 
 * Place in: /src/components/CertStatusPanel.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Lock, Loader2, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Info, ChevronDown, ChevronRight, Shield, Clock, ExternalLink
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

const STATUS_CONFIG = {
  OK: { icon: CheckCircle, color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Valid' },
  WARNING: { icon: AlertTriangle, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Expiring Soon' },
  CRITICAL: { icon: XCircle, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Expiring' },
  EXPIRED: { icon: XCircle, color: '#ef4444', bg: 'rgba(239,68,68,0.15)', label: 'Expired' },
  UNKNOWN: { icon: Info, color: '#64748b', bg: 'rgba(100,116,139,0.1)', label: 'Unknown' },
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CertStatusPanel = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [expandedDevice, setExpandedDevice] = useState(null);

  const fetchCerts = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/certs/status');
      if (res.ok) {
        const data = await res.json();
        setCerts(data.certs || []);
      }
    } catch (err) {
      console.error('Failed to fetch cert status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  if (loading) return null;
  if (certs.length === 0) return null; // Don't show panel if no certs are being monitored

  const criticalCount = certs.filter(c => c.status === 'CRITICAL' || c.status === 'EXPIRED').length;
  const warningCount = certs.filter(c => c.status === 'WARNING').length;

  return (
    <div className="md-panel cert-panel">
      <div className="md-panel-header" onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
        <div className="md-panel-header-left">
          <span className="md-panel-title">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Lock size={16} /> Certificate Status
          </span>
          <span className="md-chart-subtitle">
            {certs.length} device{certs.length !== 1 ? 's' : ''} monitored
            {criticalCount > 0 && ` · ${criticalCount} critical`}
            {warningCount > 0 && ` · ${warningCount} warning`}
          </span>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); fetchCerts(); }}>
          <RefreshCw size={12} />
        </button>
      </div>

      {expanded && (
        <div className="md-panel-body">
          <div className="cert-list">
            {certs.map(cert => {
              const cfg = STATUS_CONFIG[cert.status] || STATUS_CONFIG.UNKNOWN;
              const StatusIcon = cfg.icon;
              const isExpanded = expandedDevice === cert.deviceId;

              return (
                <div key={cert.deviceId} className={`cert-row ${cert.status?.toLowerCase()}`}>
                  <div className="cert-row-summary" onClick={() => setExpandedDevice(isExpanded ? null : cert.deviceId)}>
                    <div className="cert-row-left">
                      {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <span className="cert-status-badge" style={{ background: cfg.bg, color: cfg.color }}>
                        <StatusIcon size={11} /> {cfg.label}
                      </span>
                      <span className="cert-device-name">{cert.deviceName}</span>
                      <code className="cert-device-ip">{cert.deviceIp}</code>
                    </div>
                    <div className="cert-row-right">
                      {cert.daysRemaining !== null && cert.daysRemaining !== undefined ? (
                        <span className="cert-days" style={{ color: cfg.color }}>
                          {cert.daysRemaining <= 0 ? 'Expired' : `${cert.daysRemaining}d remaining`}
                        </span>
                      ) : (
                        <span className="cert-days" style={{ color: 'var(--text-tertiary)' }}>No cert found</span>
                      )}
                      {cert.selfSigned && <span className="cert-self-signed">Self-signed</span>}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="cert-detail">
                      <div className="cert-detail-grid">
                        <div className="cert-detail-item">
                          <label>Subject</label>
                          <span>{cert.subject || 'N/A'}</span>
                        </div>
                        <div className="cert-detail-item">
                          <label>Issuer</label>
                          <span>{cert.issuer || 'N/A'}</span>
                        </div>
                        <div className="cert-detail-item">
                          <label>Valid From</label>
                          <span>{formatDate(cert.validFrom)}</span>
                        </div>
                        <div className="cert-detail-item">
                          <label>Valid To</label>
                          <span style={{ color: cfg.color, fontWeight: 600 }}>{formatDate(cert.validTo)}</span>
                        </div>
                        <div className="cert-detail-item">
                          <label>Last Checked</label>
                          <span>{cert.lastCheckAt ? new Date(cert.lastCheckAt).toLocaleString() : 'Never'}</span>
                        </div>
                        <div className="cert-detail-item">
                          <label>Type</label>
                          <span>{cert.selfSigned ? 'Self-Signed' : 'CA-Signed'}</span>
                        </div>
                      </div>
                      {cert.lastError && (
                        <div className="cert-error">
                          <AlertTriangle size={12} /> {cert.lastError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertStatusPanel;
